import styled, { css } from 'styled-components';
import type { ProductInformationViewModel, ProductPageViewModel, SplashModel } from 'autogen/swagger/all';
import GalleryMediaPreviewer from 'components/GalleryMediaPreviewer';
import {
    type GalleryMediaItem,
    type GalleryMediaItems,
    MediaItemDiscriminator
} from 'components/GalleryMediaPreviewer/shared/types';
import SplashOverlay from 'components/product/ProductItemGallery/components/SplashOverlay';
import useUnavailableVideos from 'components/video/hooks/useUnavailableVideos';
import WhenSplitTestVariant from 'helpers/WhenSplitTestVariant';
import { useIsChanel } from 'hooks/user/useIsBrand';
import { GA4Tracking } from 'modules/tracking/GA4';
import { breakpoint, sizes } from 'variables';
import RegimeQuickViewButton from '../regime/components/RegimeQuickView/components/RegimeQuickViewButton';
import { useRegimeStore } from '../regime/shared/regimeStore';
import {
    mapAutoplayVideosToMediaItems,
    mapProductInformationModelToGalleryMediaItems,
    prependHighlightedVideoMediaItemToMediaItems
} from './shared/helpers';

const StyledGalleryMediaPreviewer = styled(GalleryMediaPreviewer)<{ $isChanel: boolean }>`
    width: 100%;
    height: 314px;
    position: relative;

    ${breakpoint.up(sizes.lg)} {
        height: 596px; // 596px = 120*4 + 12*3 + 40*2 = Thumbnail height * 4 + gap * 3 + container padding-x * 2

        ${({ $isChanel }) =>
            $isChanel &&
            css`
                height: 344px; // 344px = 80*4 + 8*3 = Thumbnail height * 4 + gap * 3
            `}
    }
`;
const pipe =
    (...fns) =>
    (x) =>
        fns.reduce((v, f) => f(v), x);

const removeUnavailableVideos = (mediaItems, unavailableVideoIds) =>
    mediaItems.filter((mediaItem) =>
        MediaItemDiscriminator(
            mediaItem,
            () => true,
            (videoItem) => !unavailableVideoIds.has(videoItem.video.id),
            (highlightedVideoItem) => !unavailableVideoIds.has(highlightedVideoItem.video.id),
            () => true,
            () => false
        )
    );

function onOpen(pswpInstance) {
    pswpInstance.on('change', () => {
        GA4Tracking({
            eventName: 'product_gallery',
            category: 'navigation',
            context: 'product_page',
            eventParams: [{ param_name: 'slide_number', param_value: pswpInstance?.currIndex + 1 }]
        });
    });

    GA4Tracking({
        eventName: 'product_gallery',
        category: 'navigation',
        context: 'product_page',
        eventParams: [{ param_name: 'toggle', param_value: true }]
    });
}

function onThumbnailClick({ index, type }: { index: number; type: GalleryMediaItem['type'] }) {
    GA4Tracking({
        context: 'product_page',
        category: 'ecommerce',
        eventName: 'click',
        eventParams: [
            {
                param_name: 'slide_number',
                param_value: index + 1
            },
            { param_name: 'modal_type', param_value: 'light_box' },
            { param_name: 'thumbnail_type', param_value: type }
        ]
    });
}

export default function ProductItemGallery({
    items,
    shouldAutoplay = false,
    splashes,
    emaLogo
}: {
    items: ProductInformationViewModel['productMedia'];
    shouldAutoplay?: boolean;
    splashes?: SplashModel;
    emaLogo?: ProductPageViewModel['emaLogo'];
}) {
    const isChanel = useIsChanel();
    const hasOneItem = items.length === 1;
    const mediaItems: GalleryMediaItems = shouldAutoplay
        ? pipe(
              mapProductInformationModelToGalleryMediaItems,
              mapAutoplayVideosToMediaItems,
              prependHighlightedVideoMediaItemToMediaItems
          )(items)
        : pipe(mapProductInformationModelToGalleryMediaItems, prependHighlightedVideoMediaItemToMediaItems)(items);

    const { unavailableVideoIds } = useUnavailableVideos();
    const filteredMediaItems = removeUnavailableVideos(mediaItems, unavailableVideoIds); // if a video fails to load/play, remove the video from mediaItems.
    const { model: regimeModel } = useRegimeStore();

    return (
        <StyledGalleryMediaPreviewer
            items={filteredMediaItems}
            options={{ hideGalleryThumbnailCarousel: !!hasOneItem }}
            onOpenCallback={(pswpInstance) => onOpen(pswpInstance)}
            onThumbnailClick={({ index, type }) => onThumbnailClick({ index, type })}
            $isChanel={isChanel}
        >
            {(splashes || emaLogo) && <SplashOverlay splashes={splashes} emaLogo={emaLogo} />}

            {regimeModel && (
                <WhenSplitTestVariant variant="Variant" systemSplitTestKey="PdpRegimesPlacement">
                    <RegimeQuickViewButton />
                </WhenSplitTestVariant>
            )}
        </StyledGalleryMediaPreviewer>
    );
}
